import Accordion from "components/blocks/Accordion";
import AccordionItem from "components/blocks/Accordion/AccordionItem";
import FilePreview from "components/blocks/FileInput/FilePreview";
import FileUploadWithPreview, {SelectedFilesType} from "components/blocks/FileUploadWithPreview";
import {Dispatch, SetStateAction, useEffect, useMemo, useRef, useState} from "react";
import {FileResult, FileUploadResponse} from "services/shared-models/sharedTypes";
import { useTranslation } from "react-i18next";
import PaginatedLookupDropdown from "components/blocks/PaginatedLookupDropdown";
import { useNotification } from "hooks/useNotification";
import LoadingOverlay from "components/blocks/LoadingOverlay";
import RefereesTable from "./ExaminersTable";
import EvaluationFormPreviewModal from "views/home/evaluationForms/EvaluationFormPreviewModal";
import {
    CourseOrderRefereeIterationResult,
    CourseOrderLocalItemResult
} from "services/courseOrder/models/CourseOrderType";
import {BackendServices} from "../../../../../../../../services";
import {GridSchema} from "../../../../../../../../components/GridView/GridView.types";
import IconButton from "../../../../../../../../components/blocks/IconButton";
import useSystemDateTheme from "../../../../../../../../hooks/useSystemDateTheme";
import Grid from "../../../../../../../../components/GridView/components/Grid";
import Modal from "../../../../../../../../components/blocks/Modal";
import AttachmentCard from "../../../../../../../../components/blocks/AttachmentCard";

export type ExaminerSelectedTemplateType = {
  title: string;
  id: number;
  currentIteration?: number;
};

const RefereesSection = ({ order,currentOrder ,orderFiles,isReferee,setTriggerOrderUpdate}:
 {
     order: CourseOrderRefereeIterationResult  | null
    currentOrder? : CourseOrderLocalItemResult | null ,
    orderFiles?: CourseOrderLocalItemResult["researcherAttachmentWithoutInfo"]
    setTriggerOrderUpdate: Dispatch<SetStateAction<boolean>>
    ,isReferee?:boolean }) => {
  const { t } = useTranslation(["Orders"]);
  const { showNotification } = useNotification();
  const {toSystemThemeDateFormat} = useSystemDateTheme();
    const [historyModal, setHistoryModal] = useState<{
    isOpen: boolean;
    files: FileResult[];
    }>({ isOpen: false, files: [] });
  const [evaluationTemplateModalVisible, setEvaluationTemplateModalVisible] = useState(false);
  const [updatingCourseEvaluationOrderIsLoading, setUpdatingCourseEvaluationOrderIsLoading] = useState(false);
  const [files, setFiles] = useState<(FileUploadResponse & { file: File; size?: string })[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ExaminerSelectedTemplateType>({
    title: "",
    id: order?.evaluationFormId ??  0,
    currentIteration: order?.id ?? 0,
  });
    const isHydrating = useRef(true);


  useEffect(() => {
    if (order) {
        isHydrating.current =true;
            setSelectedTemplate({
        title: order?.evaluationForm?.evaluationFormDetail.displayName ?? t("Common:noData"),
        id: order?.evaluationFormId ?? 0,
        currentIteration: order?.id ?? 0,
      });
        const filesIdsSet = new Set();
        const uniqueFiles = [...(order.files ?? [])].reduce((acc, file) => {
            if (!filesIdsSet.has(file.id)) {
                acc.push({
                    file: new File([], file.name ?? ""),
                    uuid: file.id,
                    fileName: file.name ?? "",
                    size: file.size,
                    showDeleteButton: true,
                    createdOn: file.createdOn,
                    createdBy: file.createdBy,
                });
                filesIdsSet.add(file.id);
            }
            return acc;
        }, [] as SelectedFilesType[]);
        setFiles(uniqueFiles);


    }
     setTimeout(()=>{isHydrating.current = false;},100) ;
  }, [order]);
    const historyFileIds = useMemo(() => {
        const ids = new Set<string>();
        const histories = order?.courseOrderIterationFileHistories ?? [];
        histories.forEach((h: any) => {
            const hFiles = h?.files ?? [];
            hFiles.forEach((f: FileResult) => {
                if (f?.id) ids.add(f.id);
            });
        });
        return ids;
    }, [order]);

    const modifiedHistories = useMemo(() => {
        console.log(order?.courseOrderIterationFileHistories);
        const all = order?.courseOrderIterationFileHistories ?? [];
        return all.filter((h: any) => h.actionType === "Updated");
    }, [order]);

    const modifiedHistoryGridSchema = useMemo<GridSchema[]>(() => {
        return [
            {
                field: "TheUpdate",
                displayName: t("TheUpdate", { ns: "Orders" }) ,
                render: (row: any,index:number) =>t("Update", { ns: "Orders" }) + ` ${index + 1}` ,
            },
            {
                field: "modifiedOn",
                displayName: t("modifiedDate", { ns: "Orders" }),
                render: (row: any) => toSystemThemeDateFormat(row?.createdOn ?? row?.modifiedOn ?? row?.date),
            },
            {
                field: "attachments",
                displayName: t("Common:attachments"),
                showOnMobile: true,
                render: (row: any) => (
                    <span className="d-flex justify-content-center">
                <IconButton
                    fitToIconWidth
                    icon="icon-eye"
                    innerIconColor="primary"
                    size="md"
                    onClick={() => {
                        const files = (row?.files as FileResult[]) ?? (row?.file ? [row.file] : []);
                        setHistoryModal({ isOpen: true, files: files ?? [] });
                    }}
                />
              </span>
                ),
            }

        ];
    }, [t, toSystemThemeDateFormat]);



    const updateCourseEvaluationOrder = async ({
    evaluationFormId,
    filesIds,
  }: {
    evaluationFormId: number;
    filesIds: string[];
  }) => {
      if (!order || isHydrating.current){
          setTriggerOrderUpdate(state=>!state);
          return;
      }
    try {
      setUpdatingCourseEvaluationOrderIsLoading(true);
      const response = await BackendServices.courseOrderService.UpdateRefereeEvaluationOrder(order.id, {
        evaluationFormId: evaluationFormId,
        filesIds: filesIds,
      });
      if (response.hasError) {
        showNotification({
          description: t(response.description),
          type: "error",
        });
      } else {
        showNotification({
          description: t("Common:success"),
          type: "success",
        });
      }
    } catch {
      showNotification({
        description: t("Common:internalServerError"),
        type: "error",
      });
    } finally {
      setUpdatingCourseEvaluationOrderIsLoading(false);
    }
  };

  return (
    <>
      <Accordion>
        <AccordionItem initiallyOpen title={t("referees")} elementId={13}>
          <div className="d-flex flex-column gap-4 border-bottom pb-4">
            <div className="d-flex flex-column gap-4 position-relative">
              <LoadingOverlay visible={updatingCourseEvaluationOrderIsLoading} />
              <PaginatedLookupDropdown
                 withAsterisk={true}
                label={t("evaluationTemplate")}
                service="evaluationFormsService"
                endpoint="getAllRefereeEvaluationForms"
                queryKey="search"
                idValueKey="id"
                textValueKey="evaluationFormDetail.displayName"
                isPaginated
                value={selectedTemplate?.id}
                onChange={async (id, item) => {
                  setSelectedTemplate({
                    title: item.label,
                    id: +id,
                    currentIteration:selectedTemplate.id
                  });
                    if (isHydrating.current) return;
                  await updateCourseEvaluationOrder({
                    evaluationFormId: +id,
                    filesIds: files.map((file) => file.uuid),
                  });
                }}
              />

              {selectedTemplate && (
                <FilePreview
                  title={selectedTemplate.title}
                  callbackValue={selectedTemplate.id}
                  onShow={() => {
                    setEvaluationTemplateModalVisible(true);
                  }}
                  showFileSize={false}
                  hideDeleteButton
                />
              )}

              {selectedTemplate && (
                <FileUploadWithPreview
                  files={files}
                  label={t("EvaluationsFileIterationLabel")}
                  setFiles={setFiles}
                  onChange={async (files) => {
                      if (isHydrating.current) return;
                      await updateCourseEvaluationOrder({
                        evaluationFormId: selectedTemplate.id,
                        filesIds: files.map((file) => file.uuid),
                      });
                  }}
                  onDelete={async (files) => {
                      if (isHydrating.current) return;
                      await updateCourseEvaluationOrder({
                        evaluationFormId: selectedTemplate.id,
                        filesIds: files.map((file) => file.uuid),
                      });
                  }}
                  formProps={{}}
                />
              )}
            </div>

            <RefereesTable
                orderId={order?.id ?? 0}
                courseOrderEvaluationId={order?.id ?? 0}
                selectedTemplate={selectedTemplate}
                 // setTriggerOrderUpdate={setTriggerOrderUpdate}
                setSelectedTemplate={setSelectedTemplate}
            />
              <div className="pb-4">
                  <h6 className="fw-bold">{ t("HistoryFiles")}</h6>
                  <div className="mt-4">
                      <Grid gridSchema={modifiedHistoryGridSchema} data={modifiedHistories} />
                  </div>
              </div>
          </div>
        </AccordionItem>
      </Accordion>


        <Modal
            containerClassName="bg-white pb-2"
            isOpen={historyModal.isOpen}
            onClose={() => setHistoryModal({ isOpen: false, files: [] })}
            title={t("Common:attachments")}
            withHeaderBorder
            size="lg"
            titleClassName="fs-6 text-dark"
        >
            <div className="d-flex gap-2 flex-wrap">
                {historyModal.files && historyModal.files.length > 0 ? (
                    historyModal.files.map((file: FileResult, index: number) => (
                        <AttachmentCard
                            key={`${file.id ?? index}`}
                            attachment={{
                                extension: file.extension ?? "",
                                fileName: file.name ?? "",
                                id: file.id ?? "",
                                size: file.size ?? "",
                            }}
                        />
                    ))
                ) : (
                    t("Common:noData")
                )}
            </div>
        </Modal>
      <EvaluationFormPreviewModal
        evaluationFormId={selectedTemplate.id}
        title={selectedTemplate.title}
        isOpen={evaluationTemplateModalVisible}
        onClose={() => setEvaluationTemplateModalVisible(false)}
        size="lg"
      />
    </>
  );
};

export default RefereesSection ;
